import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

export function SatisfactionCard({ clientId }) {
  const [surveys, setSurveys] = useState(null);

  const load = () => api.get(`/clients/${clientId}/surveys`).then((r) => setSurveys(r.data.surveys || [])).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, [clientId]); // eslint-disable-line

  const copySurveyLink = async () => {
    try {
      let links = (await api.get(`/clients/${clientId}/portal-links`)).data || [];
      if (!links.length) links = [(await api.post(`/clients/${clientId}/portal-links`)).data];
      const url = `${window.location.origin}/portal/${links[0].token}#survey`;
      try { await navigator.clipboard.writeText(url); } catch {}
      toast.success("Survey link copied", { description: url, duration: 8000 });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const list = surveys || [];
  const avg = (k) => list.length ? (list.reduce((a, s) => a + (s[k] || 0), 0) / list.length).toFixed(1) : "—";

  return (
    <div className="mt-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="satisfaction-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-heading text-lg font-semibold text-stone-900">Satisfaction survey</p>
          <p className="mt-0.5 text-xs text-stone-500">Lives on the client portal — share the link with the client or family. Nothing is emailed.</p>
        </div>
        <button onClick={copySurveyLink} className="rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-stone-900" data-testid="survey-copy-link">
          Copy survey link
        </button>
      </div>
      {surveys === null ? <p className="mt-4 text-xs text-stone-400">Loading…</p> : list.length === 0 ? (
        <p className="mt-4 rounded-lg border border-dashed border-stone-300 p-5 text-center text-xs text-stone-400" data-testid="survey-empty">
          No responses yet — copy the link and send or read it to the client/family.
        </p>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="survey-averages">
            <span className="rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs text-stone-600">{list.length} response{list.length > 1 ? "s" : ""}</span>
            <span className="rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs text-stone-600">Overall {avg("overall")}/5</span>
            <span className="rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs text-stone-600">Reliability {avg("reliability")}/5</span>
            <span className="rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs text-stone-600">Communication {avg("communication")}/5</span>
            <span className="rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs text-stone-600">Recommend: {list.filter((s) => s.recommend === "Yes").length} yes / {list.filter((s) => s.recommend === "No").length} no</span>
          </div>
          <ul className="mt-3 space-y-2" data-testid="survey-list">
            {list.map((s) => (
              <li key={s.id} className={`rounded-lg border px-3 py-2 text-xs ${s.low_flag ? "border-[#C0392B]/40 bg-[#C0392B]/5" : "border-stone-100"}`} data-testid={`survey-response-${s.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-stone-800">{String(s.submitted_at).slice(0, 10)}</span>
                  <span className="text-stone-500">{s.who}</span>
                  <span className="text-stone-500">Overall {s.overall} · Reliability {s.reliability} · Office {s.communication} · Recommend: {s.recommend}</span>
                  {s.low_flag && <span className="rounded-full border border-[#C0392B]/40 bg-[#C0392B]/10 px-2 py-0.5 text-[10px] font-bold text-[#C0392B]">follow up</span>}
                </div>
                {(s.better || s.anything) && (
                  <p className="mt-1 text-stone-600">{[s.better && `Do better: ${s.better}`, s.anything && `Also: ${s.anything}`].filter(Boolean).join(" — ")}</p>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
